/**
 * 应用初始状态与 DOM 引用收集
 */

export function createInitialState() {
  const defaultTtsVoice = 'zh-CN-XiaoxiaoNeural';
  return {
    currentView: 'shelf',
    books: [],
    thoughts: [],
    highlights: [],
    essays: [],
    bookmarks: [],
    shelfRandomHighlightId: null,
    shelfWelcomeLastTouchAt: 0,
    shelfWelcomeSwitchTimerId: null,
    currentBook: null,
    reader: null,
    isGridView: true,
    searchQuery: '',
    sortBy: 'addedAt',
    filterFormat: 'all',
    filterCategory: 'all',
    filterCollection: 'all',
    shelfQuickFilter: null,
    categories: [],
    collections: [],
    collectionMembership: {},
    selectedBooks: new Set(),
    isSelectMode: false,
    thoughtsSearch: '',
    thoughtsSelectedTags: [],
    thoughtsInputTags: [],
    thoughtsAllTags: [],
    thoughtsTagPopupVisible: false,
    highlightSearch: '',
    highlightGrouped: false,
    highlightGroupLayout: 'list',
    highlightColorFilter: 'all',
    highlightSortBy: 'createdAt',
    readingStats: {
      todayMinutes: 0,
      dailyGoal: 30,
      streak: 0,
      lastReadDate: null
    },
    readingStartTime: null,
    readerSettings: {
      fontSize: 18,
      lineHeight: 2,
      fontFamily: 'sans',
      theme: 'dark',
      autoScroll: false,
      autoScrollSpeed: 30,
      dualPage: false,
      immersiveMode: false,
      autoNightMode: true
    },
    autoScrollTimer: null,
    autoScrollPaused: false,
    readingTimer: {
      enabled: false,
      duration: 30,
      startTime: null,
      timerId: null
    },
    tts: {
      enabled: false,
      speaking: false,
      utterance: null,
      rate: 1,
      pitch: 0,
      voice: defaultTtsVoice,
      engine: 'edge',
      edgeVoice: defaultTtsVoice,
      voiceLanguageFilter: 'zh',
      voiceGenderFilter: 'Female'
    },
    textSearch: {
      query: '',
      results: [],
      currentIndex: -1
    },
    music: {
      initialized: false,
      audio: null,
      isPlaying: false,
      volume: 0.7,
      playMode: 'list',
      currentTrack: null,
      currentIndex: 0,
      duration: 0,
      currentTime: 0,
      knobAngle: 0,
      tracks: [],
      podcasts: [],
      playlistMode: 'all',
      albumExpanded: {},
      podcastExpanded: {}
    },
    auth: {
      initialized: false,
      authenticated: false,
      user: null,
      membership: {
        planCode: 'free',
        planName: 'Free',
        status: 'active',
        features: {
          canAccessAi: false,
          canAccessMusic: false,
          canImportMusic: false,
          canManageAiModels: false,
          maxBooks: 20
        }
      },
      plans: []
    },
    admin: {
      query: '',
      planCode: 'all',
      status: 'all',
      range: 'monthly',
      activeTab: 'overview',
      page: 1,
      pageSize: 20,
      total: 0,
      selectedUserId: null
    },
    globalShelfOrder: [],
    booklists: [],
    booklistMembership: {},
    booklistOrder: [],
    booklist: {
      currentBooklistId: null,
      search: '',
      bookSearch: '',
      listSortBy: 'custom',
      bookSortBy: 'custom'
    }
  };
}

export function collectAppElements(doc = document) {
  return {
    sidebar: doc.getElementById('sidebar'),
    userAvatarWrap: doc.getElementById('userAvatarWrap'),
    userAvatarBtn: doc.getElementById('userAvatarBtn'),
    userAvatarImage: doc.getElementById('userAvatarImage'),
    userAvatarFallback: doc.getElementById('userAvatarFallback'),
    userPlanBadge: doc.getElementById('userPlanBadge'),
    userMenu: doc.getElementById('userMenu'),
    userMenuName: doc.getElementById('userMenuName'),
    userMenuEmail: doc.getElementById('userMenuEmail'),
    userMembershipBtn: doc.getElementById('userMembershipBtn'),
    userLoginBtn: doc.getElementById('userLoginBtn'),
    userLogoutBtn: doc.getElementById('userLogoutBtn'),
    userAuthOverlay: doc.getElementById('userAuthOverlay'),
    userAuthTitle: doc.getElementById('userAuthTitle'),
    userAuthClose: doc.getElementById('userAuthClose'),
    userAuthEmail: doc.getElementById('userAuthEmail'),
    userAuthPassword: doc.getElementById('userAuthPassword'),
    userAuthNicknameWrap: doc.getElementById('userAuthNicknameWrap'),
    userAuthNickname: doc.getElementById('userAuthNickname'),
    userAuthSubmitBtn: doc.getElementById('userAuthSubmitBtn'),
    userAuthToggleModeBtn: doc.getElementById('userAuthToggleModeBtn'),
    membershipOverlay: doc.getElementById('membershipOverlay'),
    membershipClose: doc.getElementById('membershipClose'),
    membershipSummary: doc.getElementById('membershipSummary'),
    membershipPlans: doc.getElementById('membershipPlans'),
    adminNavItem: doc.getElementById('adminNavItem'),
    adminView: doc.getElementById('adminView'),
    adminSearchInput: doc.getElementById('adminSearchInput'),
    adminPlanFilter: doc.getElementById('adminPlanFilter'),
    adminStatusFilter: doc.getElementById('adminStatusFilter'),
    adminRangeFilter: doc.getElementById('adminRangeFilter'),
    adminRefreshBtn: doc.getElementById('adminRefreshBtn'),
    adminExportMembersCsvBtn: doc.getElementById('adminExportMembersCsvBtn'),
    adminTabs: doc.getElementById('adminTabs'),
    adminTabItems: doc.querySelectorAll('[data-admin-tab]'),
    adminPanels: doc.querySelectorAll('[data-admin-panel]'),
    adminOverviewExtra: doc.getElementById('adminOverviewExtra'),
    adminStatTotalUsers: doc.getElementById('adminStatTotalUsers'),
    adminStatActiveMembers: doc.getElementById('adminStatActiveMembers'),
    adminStatSuspendedMembers: doc.getElementById('adminStatSuspendedMembers'),
    adminStatMrr: doc.getElementById('adminStatMrr'),
    adminBatchToolbar: doc.getElementById('adminBatchToolbar'),
    adminBatchCount: doc.getElementById('adminBatchCount'),
    adminBatchPlanSelect: doc.getElementById('adminBatchPlanSelect'),
    adminBatchStatusSelect: doc.getElementById('adminBatchStatusSelect'),
    adminBatchAutoRenewSelect: doc.getElementById('adminBatchAutoRenewSelect'),
    adminBatchApplyBtn: doc.getElementById('adminBatchApplyBtn'),
    adminSelectAllRows: doc.getElementById('adminSelectAllRows'),
    adminMembersTableBody: doc.getElementById('adminMembersTableBody'),
    adminMembersEmpty: doc.getElementById('adminMembersEmpty'),
    adminMemberContext: doc.getElementById('adminMemberContext'),
    adminBillingCards: doc.getElementById('adminBillingCards'),
    adminBillingTableBody: doc.getElementById('adminBillingTableBody'),
    adminBillingEmpty: doc.getElementById('adminBillingEmpty'),
    adminFunnelCards: doc.getElementById('adminFunnelCards'),
    adminFunnelList: doc.getElementById('adminFunnelList'),
    adminRiskTableBody: doc.getElementById('adminRiskTableBody'),
    adminRiskEmpty: doc.getElementById('adminRiskEmpty'),
    adminAutoDowngradeBtn: doc.getElementById('adminAutoDowngradeBtn'),
    adminAlertsTableBody: doc.getElementById('adminAlertsTableBody'),
    adminAlertsEmpty: doc.getElementById('adminAlertsEmpty'),
    adminNotesStatusFilter: doc.getElementById('adminNotesStatusFilter'),
    adminNotesRefreshBtn: doc.getElementById('adminNotesRefreshBtn'),
    adminNotesTableBody: doc.getElementById('adminNotesTableBody'),
    adminNotesEmpty: doc.getElementById('adminNotesEmpty'),
    adminGateRulesTableBody: doc.getElementById('adminGateRulesTableBody'),
    adminGateEmpty: doc.getElementById('adminGateEmpty'),
    adminAuditActionFilter: doc.getElementById('adminAuditActionFilter'),
    adminAuditRefreshBtn: doc.getElementById('adminAuditRefreshBtn'),
    adminAuditTableBody: doc.getElementById('adminAuditTableBody'),
    adminAuditEmpty: doc.getElementById('adminAuditEmpty'),
    adminReportsRangeFilter: doc.getElementById('adminReportsRangeFilter'),
    adminReportsRefreshBtn: doc.getElementById('adminReportsRefreshBtn'),
    adminReportNewUsers: doc.getElementById('adminReportNewUsers'),
    adminReportActivatedMemberships: doc.getElementById('adminReportActivatedMemberships'),
    adminReportAdminOperations: doc.getElementById('adminReportAdminOperations'),
    adminReportCurrentMrr: doc.getElementById('adminReportCurrentMrr'),
    adminReportsMeta: doc.getElementById('adminReportsMeta'),
    navItems: doc.querySelectorAll('.nav-item[data-view]'),
    views: doc.querySelectorAll('.view'),
    shelfView: doc.getElementById('shelfView'),
    booksGrid: doc.getElementById('booksGrid'),
    emptyShelf: doc.getElementById('emptyShelf'),
    addBookBtn: doc.getElementById('addBookBtn'),
    siteThemeBtn: doc.getElementById('siteThemeBtn'),
    navMoreBtn: doc.getElementById('navMoreBtn'),
    navMoreMenu: doc.getElementById('navMoreMenu'),
    fileInput: doc.getElementById('fileInput'),
    gridViewBtn: doc.getElementById('gridViewBtn'),
    booklistBtn: doc.getElementById('booklistBtn'),
    booklistPanel: doc.getElementById('booklistPanel'),
    booklistContent: doc.getElementById('booklistContent'),
    shelfStats: doc.getElementById('shelfStats'),
    statTotal: doc.getElementById('statTotal'),
    statReading: doc.getElementById('statReading'),
    statFinished: doc.getElementById('statFinished'),
    statPlanned: doc.getElementById('statPlanned'),
    searchBox: doc.getElementById('searchBox'),
    searchInput: doc.getElementById('searchInput'),
    searchClear: doc.getElementById('searchClear'),
    sortBtn: doc.getElementById('sortBtn'),
    sortMenu: doc.getElementById('sortMenu'),
    formatTrigger: doc.getElementById('formatTrigger'),
    formatLabel: doc.getElementById('formatLabel'),
    formatPopover: doc.getElementById('formatPopover'),
    formatList: doc.getElementById('formatList'),
    categoryFilters: doc.getElementById('categoryFilters'),
    dropZone: doc.getElementById('dropZone'),
    batchActions: doc.getElementById('batchActions'),
    selectedCount: doc.getElementById('selectedCount'),
    deleteSelectedBtn: doc.getElementById('deleteSelectedBtn'),
    batchCategoryBtn: doc.getElementById('batchCategoryBtn'),
    batchCollectionBtn: doc.getElementById('batchCollectionBtn'),
    shelfWelcome: doc.getElementById('shelfWelcome'),
    welcomeGreeting: doc.getElementById('welcomeGreeting'),
    welcomeMessage: doc.getElementById('welcomeMessage'),
    statStreak: doc.getElementById('statStreak'),
    recentTimeline: doc.getElementById('recentTimeline'),
    timelineItems: doc.getElementById('timelineItems'),
    uploadProgress: doc.getElementById('uploadProgress'),
    uploadCount: doc.getElementById('uploadCount'),
    uploadBarFill: doc.getElementById('uploadBarFill'),
    uploadFileName: doc.getElementById('uploadFileName'),
    thoughtsList: doc.getElementById('thoughtsList'),
    emptyThoughts: doc.getElementById('emptyThoughts'),
    addThoughtBtn: doc.getElementById('addThoughtBtn'),
    thoughtsInput: doc.getElementById('thoughtsInput'),
    thoughtsInputPreview: doc.getElementById('thoughtsInputPreview'),
    thoughtsInputTags: doc.getElementById('thoughtsInputTags'),
    thoughtsTagBtn: doc.getElementById('thoughtsTagBtn'),
    thoughtsSendBtn: doc.getElementById('thoughtsSendBtn'),
    thoughtsImageBtn: doc.getElementById('thoughtsImageBtn'),
    thoughtsListBtn: doc.getElementById('thoughtsListBtn'),
    thoughtsOrderedListBtn: doc.getElementById('thoughtsOrderedListBtn'),
    thoughtsTodoBtn: doc.getElementById('thoughtsTodoBtn'),
    thoughtsQuoteBtn: doc.getElementById('thoughtsQuoteBtn'),
    thoughtsMapBtn: doc.getElementById('thoughtsMapBtn'),
    thoughtsVoiceBtn: doc.getElementById('thoughtsVoiceBtn'),
    thoughtsImageInput: doc.getElementById('thoughtsImageInput'),
    thoughtsVideoInput: doc.getElementById('thoughtsVideoInput'),
    thoughtsCount: doc.getElementById('thoughtsCount'),
    thoughtsSearchInput: doc.getElementById('thoughtsSearchInput'),
    thoughtsHeatmap: doc.getElementById('thoughtsHeatmap'),
    thoughtsTagsList: doc.getElementById('thoughtsTagsList'),
    thoughtsTagAddBtn: doc.getElementById('thoughtsTagAddBtn'),
    thoughtsTimeline: doc.getElementById('thoughtsTimeline'),
    thoughtsSidebar: doc.getElementById('thoughtsSidebar'),
    highlightsList: doc.getElementById('highlightsList'),
    emptyHighlights: doc.getElementById('emptyHighlights'),
    highlightsStats: doc.getElementById('highlightsStats'),
    highlightTotal: doc.getElementById('highlightTotal'),
    highlightBooks: doc.getElementById('highlightBooks'),
    highlightChars: doc.getElementById('highlightChars'),
    highlightSearchInput: doc.getElementById('highlightSearchInput'),
    highlightSortBtn: doc.getElementById('highlightSortBtn'),
    highlightGroupBtn: doc.getElementById('highlightGroupBtn'),
    highlightColorFilters: doc.getElementById('highlightColorFilters'),
    exportHighlightsBtn: doc.getElementById('exportHighlightsBtn'),
    essaysSidebar: doc.getElementById('essaysSidebar'),
    essaysList: doc.getElementById('essaysList'),
    emptyEssays: doc.getElementById('emptyEssays'),
    addEssayBtn: doc.getElementById('addEssayBtn'),
    essaysEditorArea: doc.getElementById('essaysEditorArea'),
    essaysEditorHeader: doc.getElementById('essaysEditorHeader'),
    essaysEditorContent: doc.getElementById('essaysEditorContent'),
    essayTitleInput: doc.getElementById('essayTitleInput'),
    deleteEssayBtn: doc.getElementById('deleteEssayBtn'),
    notionEditorContainer: doc.getElementById('notionEditorContainer'),
    essaysSidebarToggle: doc.getElementById('essaysSidebarToggle'),
    readerOverlay: doc.getElementById('readerOverlay'),
    readerTitle: doc.getElementById('readerTitle'),
    readerContent: doc.getElementById('readerContent'),
    closeReaderBtn: doc.getElementById('closeReaderBtn'),
    prevPageBtn: doc.getElementById('prevPageBtn'),
    nextPageBtn: doc.getElementById('nextPageBtn'),
    progressFill: doc.getElementById('progressFill'),
    progressText: doc.getElementById('progressText'),
    highlightBtn: doc.getElementById('highlightBtn'),
    thoughtBtn: doc.getElementById('thoughtBtn'),
    tocBtn: doc.getElementById('tocBtn'),
    tocSidebar: doc.getElementById('tocSidebar'),
    tocCloseBtn: doc.getElementById('tocCloseBtn'),
    tocList: doc.getElementById('tocList'),
    tocSearchResults: doc.getElementById('tocSearchResults'),
    tocSearchInput: doc.getElementById('tocSearchInput'),
    tocSearchClear: doc.getElementById('tocSearchClear'),
    chapterName: doc.getElementById('chapterName'),
    selectionToolbar: doc.getElementById('selectionToolbar'),
    selToolBookmark: doc.getElementById('selToolBookmark'),
    selToolHighlight: doc.getElementById('selToolHighlight'),
    selToolDict: doc.getElementById('selToolDict'),
    selToolMarker: doc.getElementById('selToolMarker'),
    selToolUnderline: doc.getElementById('selToolUnderline'),
    selToolWavy: doc.getElementById('selToolWavy'),
    selectionDictPopover: doc.getElementById('selectionDictPopover'),
    selectionDictWordInput: doc.getElementById('selectionDictWordInput'),
    selectionDictSearchBtn: doc.getElementById('selectionDictSearchBtn'),
    selectionDictHistoryBtn: doc.getElementById('selectionDictHistoryBtn'),
    selectionDictFavoriteBtn: doc.getElementById('selectionDictFavoriteBtn'),
    selectionDictCopyBtn: doc.getElementById('selectionDictCopyBtn'),
    selectionDictCloseBtn: doc.getElementById('selectionDictCloseBtn'),
    selectionDictBody: doc.getElementById('selectionDictBody'),
    fontSizeBtn: doc.getElementById('fontSizeBtn'),
    fontPanel: doc.getElementById('fontPanel'),
    fontDecBtn: doc.getElementById('fontDecBtn'),
    fontIncBtn: doc.getElementById('fontIncBtn'),
    fontSizeValue: doc.getElementById('fontSizeValue'),
    themeBtn: doc.getElementById('themeBtn'),
    themePanel: doc.getElementById('themePanel'),
    fullscreenBtn: doc.getElementById('fullscreenBtn'),
    shortcutsHint: doc.getElementById('shortcutsHint'),
    searchBtn: doc.getElementById('searchBtn'),
    searchPanel: doc.getElementById('searchPanel'),
    readerSearchInput: doc.getElementById('readerSearchInput'),
    searchPrevBtn: doc.getElementById('searchPrevBtn'),
    searchNextBtn: doc.getElementById('searchNextBtn'),
    searchResultCount: doc.getElementById('searchResultCount'),
    bookmarkBtn: doc.getElementById('bookmarkBtn'),
    bookmarksPanel: doc.getElementById('bookmarksPanel'),
    bookmarksList: doc.getElementById('bookmarksList'),
    addBookmarkBtn: doc.getElementById('addBookmarkBtn'),
    ttsBtn: doc.getElementById('ttsBtn'),
    ttsPanel: doc.getElementById('ttsPanel'),
    ttsSlower: doc.getElementById('ttsSlower'),
    ttsFaster: doc.getElementById('ttsFaster'),
    ttsRateValue: doc.getElementById('ttsRateValue'),
    ttsPitchLower: doc.getElementById('ttsPitchLower'),
    ttsPitchHigher: doc.getElementById('ttsPitchHigher'),
    ttsPitchValue: doc.getElementById('ttsPitchValue'),
    ttsVoiceLanguageSelect: doc.getElementById('ttsVoiceLanguageSelect'),
    ttsVoiceGenderSelect: doc.getElementById('ttsVoiceGenderSelect'),
    ttsVoiceSelect: doc.getElementById('ttsVoiceSelect'),
    toggleTTSBtn: doc.getElementById('toggleTTSBtn'),
    autoScrollBtn: doc.getElementById('autoScrollBtn'),
    autoScrollPanel: doc.getElementById('autoScrollPanel'),
    autoScrollSlower: doc.getElementById('autoScrollSlower'),
    autoScrollFaster: doc.getElementById('autoScrollFaster'),
    autoScrollSpeedValue: doc.getElementById('autoScrollSpeedValue'),
    toggleAutoScrollBtn: doc.getElementById('toggleAutoScrollBtn'),
    timerBtn: doc.getElementById('timerBtn'),
    timerPanel: doc.getElementById('timerPanel'),
    timerStatus: doc.getElementById('timerStatus'),
    immersiveBtn: doc.getElementById('immersiveBtn'),
    dualPageBtn: doc.getElementById('dualPageBtn'),
    readingEstimate: doc.getElementById('readingEstimate'),
    modalOverlay: doc.getElementById('modalOverlay'),
    modal: doc.getElementById('modal'),
    modalTitle: doc.getElementById('modalTitle'),
    modalBody: doc.getElementById('modalBody'),
    modalFooter: doc.getElementById('modalFooter'),
    modalClose: doc.getElementById('modalClose'),
    settingsBtn: doc.getElementById('settingsBtn'),
    settingsOverlay: doc.getElementById('settingsOverlay'),
    settingsClose: doc.getElementById('settingsClose'),
    aiModelsList: doc.getElementById('aiModelsList'),
    addAiModelBtn: doc.getElementById('addAiModelBtn'),
    exportAiModelsBtn: doc.getElementById('exportAiModelsBtn'),
    importAiModelsBtn: doc.getElementById('importAiModelsBtn'),
    importAiModelsFile: doc.getElementById('importAiModelsFile'),
    aiModelForm: doc.getElementById('aiModelForm'),
    cancelAiModelBtn: doc.getElementById('cancelAiModelBtn'),
    saveAiModelBtn: doc.getElementById('saveAiModelBtn'),
    aiFloatBtn: doc.getElementById('aiFloatBtn'),
    aiChatPanel: doc.getElementById('aiChatPanel'),
    aiChatClose: doc.getElementById('aiChatClose'),
    aiNewChatBtn: doc.getElementById('aiNewChatBtn'),
    aiHistoryBtn: doc.getElementById('aiHistoryBtn'),
    aiModelDropdown: doc.getElementById('aiModelDropdown'),
    aiModelTrigger: doc.getElementById('aiModelTrigger'),
    aiModelSelected: doc.getElementById('aiModelSelected'),
    aiModelOptions: doc.getElementById('aiModelOptions'),
    aiChatMessages: doc.getElementById('aiChatMessages'),
    aiChatInput: doc.getElementById('aiChatInput'),
    aiSendBtn: doc.getElementById('aiSendBtn')
  };
}
